import React, { useContext, useEffect, useState } from 'react';
import { Formik } from 'formik';
import { TextField, Button, Theme, IconButton } from '@mui/material';
import { HttpStatuses, ValidateEmail, ErrorCodes } from '../types';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Alert from '@mui/lab/Alert';
import ClearIcon from '@mui/icons-material/Clear';
import { GlobalErrorContext } from '../context';
import passwordReset from '../lib/services/passwordReset';

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    width: '100%', // Fix IE 11 issue.
    marginTop: theme.spacing(1)
  },
  submit: {
    margin: theme.spacing(3, 0, 2)
  }
}));

interface Props {
  handleSuccess: () => void;
  handleFailure: () => void;
}

/**
 * Validate Email Form
 * @param props
 */
export const ValidateEmailForm: React.FC<Props> = ({
  handleSuccess,
  handleFailure
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { setGlobalError } = useContext(GlobalErrorContext);
  const [notFoundError, setNotFoundError] = useState('');

  useEffect(() => {
    if (notFoundError) {
      const timeId = setTimeout(() => {
        setNotFoundError('');
      }, 15000);

      return () => {
        clearTimeout(timeId);
      };
    }
  }, [notFoundError]);

  return (
    <Formik
      initialValues={{ email: '' } as ValidateEmail}
      onSubmit={async (values, { setErrors, setSubmitting }) => {
        let response;
        const returnedValue: ValidateEmail = {
          email: values.email ?? ''
        };
        response = await passwordReset(returnedValue);
        setSubmitting(false);
        if (response.error) {
          if (response.error.code !== ErrorCodes.UNAUTHORIZED_USER) {
            switch (response.error.code) {
              case ErrorCodes.NOT_FOUND:
                let code = t(response.error.error) ?? t('notFoundError');
                setNotFoundError(code);
                return;
              default:
                setGlobalError(response.error);
                return;
            }
          }
          return;
        }
        if (response.status === HttpStatuses.success) {
          handleSuccess();
        } else {
          handleFailure();
        }
      }}
      validate={values => {
        const errors: { [key: string]: any } = {};
        if (!values.email) {
          errors.email = t('enterValue');
        }
        return errors;
      }}
    >
      {({
        handleSubmit,
        handleChange,
        handleBlur,
        values,
        errors,
        isValid,
        isSubmitting
      }) => (
        <form onSubmit={handleSubmit} className={classes.form} noValidate>
          <TextField
            variant="standard"
            margin="normal"
            required
            fullWidth
            label={t('email')}
            name="email"
            autoComplete="email"
            autoFocus
            onChange={handleChange}
            onBlur={handleBlur}
            value={values.email}
            error={!!errors.email}
            helperText={errors.email}
          />
          <Button
            type="submit"
            fullWidth
            variant="contained"
            color="primary"
            sx={{ margin: '24px 0 16px' }}
            disabled={isSubmitting || !isValid}
          >
            {t('continue')}
          </Button>
          {notFoundError && (
            <Alert
              severity="error"
              action={
                <IconButton
                  aria-label="close"
                  color="inherit"
                  size="small"
                  onClick={() => {
                    setNotFoundError('');
                  }}
                >
                  <ClearIcon fontSize="inherit" />
                </IconButton>
              }
            >
              {t('notFoundError')}
            </Alert>
          )}
        </form>
      )}
    </Formik>
  );
};
